import { useState } from 'react';
import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/Battlefield.module.css';
import NumberInput from './NumberInput';
import { describeConditionModifiers } from '../engine/conditionModifiers';
import { COND_NAMES, decodeCond } from '../engine/conditions';
import { mergeSurfaceGrant } from '../engine/battlefield/surfaceTraits';
import { MOTION_LABELS, SURFACE_MOTIONS, surfaceMotion } from '../engine/battlefield/zones';
import AddConditionModal from './AddConditionModal';
import AddExposureModal from './AddExposureModal';
import { parseExposure, buildExposureCode } from '../engine/exposures';
import SurfaceTraitModal from './SurfaceTraitModal';
import { EditorToggle, NameColourFields, OptionRow } from './battlefieldBuild/editorParts';

/**
 * What the condition picker is shown when the thing being described is
 * ground. It has no traits, so nothing is immune and everything is
 * offered - which is what a GM defining a surface wants.
 */
const SURFACE_STUB = { bodyType: 'humanoid', conditions: [], armour: {}, wounds: { current: 1, max: 1 } };

const GOING = [
  { value: 'normal', label: 'Normal', hint: 'Crossing it costs nothing extra' },
  { value: 'difficult', label: 'Rough', hint: 'Double cost, the existing difficult-going rule' },
  { value: 'impassable', label: 'Impassable', hint: 'Nobody walks across it at all' },
];

/**
 * How the ground moves on the display (plan map-display, F). "Derived" is what it does - On Fire burns, a
 * hazard crawls, rough going bubbles, cover sways - and names what that is; a pick overrides it. Looks only.
 */
function MotionRow({ surface, set }) {
  const derived = surfaceMotion({ ...surface, motion: undefined });
  const options = [
    { value: 'derived', label: `Derived (${MOTION_LABELS[derived]})`, hint: 'From what the surface does' },
    ...SURFACE_MOTIONS.map((m) => ({ value: m, label: MOTION_LABELS[m], hint: 'On the display, whatever it does' })),
  ];
  return (
    <OptionRow
      label="Motion"
      options={options}
      value={surface.motion ?? 'derived'}
      testPrefix="motion-"
      // Derived is ABSENT, never `motion: undefined`: a literal undefined fails the whole Firestore write.
      onPick={(m) => set(m === 'derived' ? { motion: null } : { motion: m })}
    />
  );
}

/** Whose feet it catches. Off means the usual exemption applies. */
const REACHES = [
  { field: 'flying', label: 'Flyers' },
  { field: 'hovering', label: 'Hoverers' },
  { field: 'burrowing', label: 'Burrowers' },
];

/**
 * What these condition codes actually do, in words.
 *
 * The codes are the app's own vocabulary - `Pen:agi=-10`, `Fi` - which is
 * powerful and opaque in equal measure. Showing the parsed meaning under
 * the field is what stops it being a magic string: if a GM mistypes,
 * they see the effect disappear rather than discovering it mid-fight.
 */
function explain(codes) {
  if (!codes.length) return null;
  const modifiers = describeConditionModifiers({ conditions: codes });
  const named = codes.map((c) => COND_NAMES[c.split(':')[0]]).filter(Boolean);
  const parts = [...new Set([...named, ...modifiers])];
  return parts.length ? parts.join(' · ') : 'nothing this app recognises';
}

/**
 * Off is ABSENT, not `false` (v8.310.8). A `false` survived every "does
 * this surface lend anything" check and was then spread onto the
 * combatant over their own number - Regeneration 2 became `false`.
 */
function withoutTrait(traits, field) {
  const { [field]: _off, ...rest } = traits ?? {};
  return rest;
}

const COVER_LEVELS = [
  ['none', 'Nothing'],
  ['low', 'Legs (low)'],
  ['half', 'Legs + body (half)'],
  ['threeQuarter', '+ arms (three-quarter)'],
  ['full', 'All of them (full)'],
];

/**
 * A cover is a PAIR: the LEVEL says which hit locations it protects, the
 * AP says how much damage it soaks there. An AP with no level soaks on no
 * locations, which is how a surface set to give cover gave none (v7.66.0).
 *
 * COVER, NOT HARDNESS - and deliberately unlike the material editor. A
 * structure's cover AP really is its hardness, so that is what it is
 * called there. Ground protects by concealment, depth, smoke or water;
 * calling that number "hardness" would describe none of them.
 */
function CoverFields({ surface, set }) {
  const covers = surface.coverLevel && surface.coverLevel !== 'none';
  return (
    <>
      <label className={styles.buildField}>
        Cover hides
        <select
          className={common.input}
          aria-label="Surface cover level"
          value={surface.coverLevel ?? 'none'}
          onChange={(e) => set({ coverLevel: e.target.value })}
          title="Which hit locations it protects: low = legs, half = + body, three-quarter = + arms, full = + head"
        >
          {COVER_LEVELS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      {covers && (
        <label className={styles.buildField}>
          Cover AP
          <NumberInput
            className={common.input}
            aria-label="Surface cover AP"
            min={0}
            max={10}
            value={surface.coverAp ?? 0}
            onChange={(v) => set({ coverAp: Math.max(0, Math.min(10, v ?? 0)) })}
            title="Damage it soaks on the locations above, added to their armour"
          />
        </label>
      )}
    </>
  );
}

/** A lit chip that removes what it names when pressed. */
function RemoveChip({ testId, onRemove, children }) {
  return (
    <button
      type="button"
      className={clsx(styles.brush, styles.brushActive)}
      data-testid={testId}
      onClick={onRemove}
      title="Remove"
    >
      {children} ✕
    </button>
  );
}

/** One "+ Condition / Trait / Exposure" button, opening its picker. */
function AddPicker({ testId, kind, setPicking, children }) {
  return (
    <button type="button" className={common.btnSmall} data-testid={testId} onClick={() => setPicking(kind)}>
      {children}
    </button>
  );
}

/**
 * The effects, PICKED rather than typed. `AddConditionModal` already knows
 * every condition, its durations, its locations and its immunities - a
 * text box of codes asked a GM to know all of that from memory.
 *
 * WHAT THE GROUND MAKES OF THEM (v8.356.0): open ground silhouettes, smoke
 * shrouds. An exposure a surface grants is derived from where they stand,
 * like every grant here, so the picker's durations are dropped.
 */
function GrantsRow({ grants, set, setPicking }) {
  return (
    <>
      <div className={styles.brushRow} role="group" aria-label="Surface effects">
        <span className={styles.buildHint}>Effects while standing on it</span>
        {grants.map((code) => (
          <RemoveChip
            key={code}
            testId={`surface-grant-${code}`}
            onRemove={() => set({ grants: grants.filter((g) => g !== code) })}
          >
            {decodeCond(code).label}
          </RemoveChip>
        ))}
        <AddPicker testId="surface-add-condition" kind="conditions" setPicking={setPicking}>
          + Condition
        </AddPicker>
        <AddPicker testId="surface-add-trait" kind="traits" setPicking={setPicking}>
          + Trait
        </AddPicker>
        <AddPicker testId="surface-add-exposure" kind="exposures" setPicking={setPicking}>
          + Exposure
        </AddPicker>
      </div>
      <span className={styles.buildHint} data-testid="surface-effect-summary">
        {explain(grants) ?? 'No effect - a colour and a name.'}
      </span>
    </>
  );
}

/** The traits the ground lends, each removable - when it lends any. */
function LentTraits({ traits, set }) {
  const lent = Object.entries(traits ?? {}).filter(([, v]) => v);
  if (!lent.length) return null;
  return (
    <div className={styles.brushRow} role="group" aria-label="Granted traits">
      <span className={styles.buildHint}>Lends</span>
      {lent.map(([field]) => (
        <RemoveChip
          key={field}
          testId={`surface-trait-${field}`}
          onRemove={() => set({ traits: withoutTrait(traits, field) })}
        >
          {field}
        </RemoveChip>
      ))}
    </div>
  );
}

/**
 * A flyer is over it and a burrower under it, so neither is caught unless
 * the surface says so. A warp rift is not something you fly over.
 */
function ReachesRow({ surface, set }) {
  return (
    <div className={styles.brushRow} role="group" aria-label="Also reaches">
      <span className={styles.buildHint}>Also reaches</span>
      {REACHES.map((r) => (
        <label key={r.field} className={clsx(common.label, common.checkRow)}>
          <input
            type="checkbox"
            aria-label={`Reaches ${r.label}`}
            checked={!!surface.affects?.[r.field]}
            onChange={(e) => set({ affects: { ...(surface.affects ?? {}), [r.field]: e.target.checked } })}
          />
          {r.label}
        </label>
      ))}
    </div>
  );
}

function SurfaceFooter({ surface, set, canDelete, onAdd, onDelete }) {
  return (
    <div className={styles.brushRow}>
      <label className={clsx(common.label, common.checkRow)}>
        <input
          type="checkbox"
          aria-label="Hazardous"
          checked={!!surface.hazard}
          onChange={(e) => set({ hazard: e.target.checked })}
        />
        Hazardous (a note to the table)
      </label>
      <label className={clsx(common.label, common.checkRow)} title="Lit ground, when the scene is lit in areas">
        <input
          type="checkbox"
          aria-label="Lit"
          data-testid="surface-light"
          checked={!!surface.light}
          onChange={(e) => set({ light: e.target.checked || null })}
        />
        Lit (the pool of a lamp, when the scene is lit in areas)
      </label>
      <button type="button" className={common.btnSmall} data-testid="surface-add" onClick={onAdd}>
        + New surface
      </button>
      <button
        type="button"
        className={common.btnSmall}
        data-testid="surface-delete"
        onClick={() => onDelete(surface.key)}
        disabled={!canDelete}
      >
        Delete
      </button>
    </div>
  );
}

/**
 * The open picker, if any. A target-shaped stub for the condition picker:
 * it checks immunity against trait fields, and ground has none - so
 * everything is eligible, which is the right answer for a surface.
 *
 * Ground keeps no timer (`timeless`): a condition it imposes is derived
 * from where somebody stands and ends when they walk out. Picking again
 * REPLACES: Fatigue 2 then Fatigue 3 is a correction, and letting both
 * stand made the ground impose Fatigue 5, because same-type conditions
 * stack (v7.78.0).
 */
function SurfacePicker({ picking, surface, grants, set, onClose }) {
  if (picking === 'conditions') {
    return (
      <AddConditionModal
        target={SURFACE_STUB}
        timeless
        onAdd={(code) => set({ grants: mergeSurfaceGrant(grants, code) })}
        onClose={onClose}
      />
    );
  }
  if (picking === 'exposures') {
    const onAdd = (code) => {
      const ex = parseExposure(code);
      set({ grants: mergeSurfaceGrant(grants, ex ? buildExposureCode({ ...ex, rounds: 0 }) : code) });
    };
    return <AddExposureModal onAdd={onAdd} onClose={onClose} />;
  }
  if (picking !== 'traits') return null;
  const onToggle = (field, value) =>
    set({ traits: value ? { ...(surface.traits ?? {}), [field]: value } : withoutTrait(surface.traits, field) });
  return <SurfaceTraitModal traits={surface.traits} onToggle={onToggle} onClose={onClose} />;
}

/** The open panel: every field of the surface. */
function SurfacePanel({ surface, set, grants, setPicking, canDelete, onAdd, onDelete }) {
  return (
    <div className={styles.materialPanel}>
      <div className={styles.buildRow}>
        <NameColourFields
          what="Surface"
          name={surface.label}
          colour={surface.colour}
          onName={(label) => set({ label })}
          onColour={(colour) => set({ colour })}
        />
        <CoverFields surface={surface} set={set} />
      </div>
      <OptionRow
        label="Going"
        options={GOING}
        value={surface.going}
        testPrefix="going-"
        onPick={(going) => set({ going })}
      />
      <GrantsRow grants={grants} set={set} setPicking={setPicking} />
      <LentTraits traits={surface.traits} set={set} />
      <ReachesRow surface={surface} set={set} />
      <MotionRow surface={surface} set={set} />
      <SurfaceFooter surface={surface} set={set} canDelete={canDelete} onAdd={onAdd} onDelete={onDelete} />
    </div>
  );
}

/**
 * SurfaceEditor - defining ground that does something.
 *
 * Mirrors `MaterialEditor` deliberately: same collapsed-until-opened
 * panel, same `{ onChange, onAdd, onDelete }` shape. A GM who
 * has edited a material already knows how to work this.
 */
export default function SurfaceEditor({ surfaces, selected, onChange, onAdd, onDelete }) {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(null);
  const surface = surfaces[selected];
  if (!surface) return null;
  // A field set to null is taken OFF the surface (absent, never undefined: Firestore refuses undefined).
  const set = (fields) =>
    onChange(Object.fromEntries(Object.entries({ ...surface, ...fields }).filter(([, v]) => v !== null)));
  const grants = surface.grants ?? [];
  const panel = { surface, set, grants, setPicking, onAdd, onDelete, canDelete: Object.keys(surfaces).length > 1 };
  return (
    <div className={styles.materialEditor} data-testid="surface-editor">
      <EditorToggle open={open} setOpen={setOpen} testId="surface-editor-toggle">
        ✎ Edit surface
      </EditorToggle>
      {open && <SurfacePanel {...panel} />}
      <SurfacePicker picking={picking} surface={surface} grants={grants} set={set} onClose={() => setPicking(null)} />
    </div>
  );
}
